import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import AdminApiTable from "@/modules/racket_lab_admin/AdminApiTable";
import ConfirmActionDialog from "@/modules/racket_lab_admin/ConfirmActionDialog";
import QrTokenMintForm from "@/modules/racket_lab_admin/QrTokenMintForm";
import useAdminApi from "@/hooks/useAdminApi";
import useAdminMutation from "@/hooks/useAdminMutation";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";
import { TOKEN_KIND_LABEL_KEY, labelKeyFor } from "@/i18n/racketLabMaps";

/**
 * RLAdminQrTokens — Sub-sprint C list + mint.
 *
 * Internal-only. Mint produces a real DB token (no QR image rendered,
 * raw token only shown via click-to-reveal in the detail page).
 */
const RLAdminQrTokens = () => {
  const t = useRacketLabAdminT();
  const navigate = useNavigate();
  const [filterKind, setFilterKind] = useState("");
  const [filterRevoked, setFilterRevoked] = useState("");
  const params = {};
  if (filterKind) params.kind = filterKind;
  if (filterRevoked) params.revoked = filterRevoked === "true";

  const { data, loading, error, reload } = useAdminApi("/admin/qr-tokens", { params });
  const rows = data?.items || [];
  const { run, loading: mutating } = useAdminMutation();
  const [draft, setDraft] = useState({
    kind: "ticket",
    issued_for_organisation_id: "TEAMSOFTWAREINNOVATIONS",
    entity_id: "",
    ttl_seconds: "",
  });

  const onMint = async () => {
    const body = {
      kind: draft.kind,
      issued_for_organisation_id: draft.issued_for_organisation_id,
    };
    if (draft.entity_id?.trim()) body.entity_id = draft.entity_id.trim();
    if (draft.ttl_seconds && Number(draft.ttl_seconds) > 0) {
      body.ttl_seconds = Number(draft.ttl_seconds);
    }
    const minted = await run("post", "/admin/qr-tokens", body);
    reload();
    if (minted?.id) navigate(`/admin/racket-lab/qr-tokens/${minted.id}`);
  };

  const columns = [
    { key: "id", label: t("common.columns.id"), render: (r) => (<code className="font-mono text-[10px]">{r.id?.slice(0, 12)}</code>) },
    { key: "kind", label: t("qr_tokens.columns.kind"), render: (r) => (<span className="text-xs">{t(labelKeyFor(TOKEN_KIND_LABEL_KEY, r.kind))}</span>) },
    {
      key: "revoked",
      label: t("qr_tokens.columns.state"),
      render: (r) => (
        <span data-testid={`rl-qr-state-${r.id}`} className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${r.revoked ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"}`}>
          {r.revoked ? t("qr_tokens.detail.state_revoked") : t("qr_tokens.detail.state_active")}
        </span>
      ),
    },
    { key: "expires_at", label: t("qr_tokens.columns.expires"), render: (r) => (<span className="text-[10px] text-slate-500">{r.expires_at?.slice(0, 19).replace("T", " ") || "—"}</span>) },
    { key: "issued_for_organisation_id", label: t("qr_tokens.mint.org_label"), render: (r) => (<code className="text-[10px] text-slate-500">{(r.issued_for_organisation_id || "").slice(0, 16)}</code>) },
  ];

  return (
    <div data-testid="rl-admin-qr-tokens-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />

      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{t("qr_tokens.list_title")}</h1>
          <p className="text-xs text-slate-500">{t("banners.requires_admin")} · {t("banners.pilot_mode_secondary")}</p>
        </div>

        <ConfirmActionDialog
          dataTestId="rl-qr-mint-confirm"
          trigger={
            <button
              data-testid="rl-qr-tokens-mint-btn"
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
              disabled={mutating}
            >
              <Plus className="w-3.5 h-3.5" /> {t("qr_tokens.mint.submit")}
            </button>
          }
          title={t("qr_tokens.mint.title")}
          description={t("qr_tokens.mint.title")}
          confirmLabel={t("qr_tokens.mint.submit")}
          onConfirm={onMint}
        >
          <QrTokenMintForm value={draft} onChange={setDraft} disabled={mutating} />
        </ConfirmActionDialog>
      </header>

      <div data-testid="rl-qr-tokens-filters" className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-600">{t("qr_tokens.mint.kind_label")}</span>
          <select
            data-testid="rl-qr-tokens-filter-kind"
            value={filterKind}
            onChange={(e) => setFilterKind(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">{t("common.filters.any_option")}</option>
            <option value="ticket">{t(TOKEN_KIND_LABEL_KEY.ticket)}</option>
            <option value="shop">{t(TOKEN_KIND_LABEL_KEY.shop)}</option>
            <option value="equipment">{t(TOKEN_KIND_LABEL_KEY.equipment)}</option>
            <option value="customer_checkin">{t(TOKEN_KIND_LABEL_KEY.customer_checkin)}</option>
            <option value="customer_handover">{t(TOKEN_KIND_LABEL_KEY.customer_handover)}</option>
            <option value="worker_personal">{t(TOKEN_KIND_LABEL_KEY.worker_personal)}</option>
            <option value="event_checkin">{t(TOKEN_KIND_LABEL_KEY.event_checkin)}</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-600">{t("qr_tokens.columns.state")}</span>
          <select
            data-testid="rl-qr-tokens-filter-revoked"
            value={filterRevoked}
            onChange={(e) => setFilterRevoked(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">{t("common.filters.any_option")}</option>
            <option value="false">{t("qr_tokens.detail.state_active")}</option>
            <option value="true">{t("qr_tokens.detail.state_revoked")}</option>
          </select>
        </label>
        <span className="ml-auto text-[10px] text-slate-500">
          {t("common.list.counter_of_total", { count: rows.length, total: data?.total ?? "—" })}
        </span>
      </div>

      <AdminApiTable
        columns={columns}
        rows={rows}
        loading={loading}
        error={error}
        emptyLabel={t("qr_tokens.empty")}
        onRowClick={(r) => navigate(`/admin/racket-lab/qr-tokens/${r.id}`)}
        dataTestId="rl-qr-tokens-table"
      />
    </div>
  );
};

export default RLAdminQrTokens;
